import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Action, Pressable, Text, useDevice, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { L10N } from './Footer.l10n';
import * as style from './Footer.module.css';
import { TYPE } from '../../../Core.constants';
import { EVENT, getPrivacyPolicy } from '../../helpers';
import { Skeleton } from '../Skeleton';

const Footer = ({ logo: showLogo = false, ...others }) => {
  const { isMobile } = useDevice();
  const { locale, translate } = useLocale();
  const {
    value: {
      finder: { place: { id: [placeId] = [], isHotel } = {} } = {},
      hotel: { features = {}, id } = {},
      id: instanceId,
      language,
      skeleton = false,
      type,
    } = {},
  } = useStore();

  const [paddingBottom, setPaddingBottom] = useState();

  useEffect(() => {
    const handleHeightChange = ({ height } = {}) => setPaddingBottom(height);

    Event.subscribe(EVENT.FOOTER_HEIGHT_CHANGE, handleHeightChange);

    return () => Event.unsubscribe(EVENT.FOOTER_HEIGHT_CHANGE, handleHeightChange);
  }, []);

  const handleUrl = () => {
    window.location = url;
  };

  const {
    localizedUrls = {},
    logo,
    url: defaultUrl,
  } = (placeId ? (isHotel ? features[placeId] : { ...features[instanceId] }) : features[id]) || {};
  const linkProps = { target: '_blank', small: true, underline: true, className: style.link };
  const { name: privacyName = '', url: privacyUrl } = getPrivacyPolicy({
    chainId: instanceId,
    features,
    genericName: translate(L10N.LABEL_GENERIC_NAME),
    hotelId: isHotel && placeId ? placeId : id,
    isChain: type === TYPE.CHAIN,
    language,
    locale,
  });
  const url =
    localizedUrls?.[language] ||
    defaultUrl ||
    features[id]?.localizedUrls?.[language] ||
    features[id]?.url ||
    window.location.origin;

  return (
    <View
      row={!isMobile}
      {...others}
      className={[style.container, others.className]}
      style={paddingBottom ? { paddingBottom } : undefined}
    >
      <View row={!isMobile} className={style.content}>
        <View row={!isMobile} className={style.info}>
          {showLogo && logo && !isMobile && (
            <Pressable onPress={url ? handleUrl : undefined}>
              <View tag="img" src={logo} className={style.logo} />
            </Pressable>
          )}

          <Text light small className={style.copyright}>
            {`© ${new Date().getFullYear()} ${privacyName}${privacyName.endsWith('.') ? '' : '.'}`}
          </Text>

          {skeleton ? (
            <Skeleton small words={5} />
          ) : (
            <Text light small>
              {translate(L10N.LABEL_ALL_RIGHTS_RESERVED)}
            </Text>
          )}

          {privacyUrl && (
            <Action href={privacyUrl} {...linkProps}>
              {translate(L10N.ACTION_PRIVACY_POLICY)}
            </Action>
          )}
        </View>

        {!features.hidePoweredByMirai && (
          <Action href="https://www.mirai.com/" small target="_blank" className={style.powered}>
            Powered by{' '}
            <Text bold light className={style.mirai}>
              mirai
            </Text>
          </Action>
        )}
      </View>
    </View>
  );
};

Footer.displayName = 'Mirai:Core:Footer';

Footer.propTypes = {
  logo: PropTypes.bool,
};

export { Footer };
